import React, { useEffect, useState } from "react";
import { Form } from "react-final-form";
import { Computer, CopyAll } from "@mui/icons-material";
import { Box, Button, LinearProgress, Typography } from "@mui/material";
import { useSnackbar } from "notistack";

import { LoadingButton } from "ui";

import { apiSlice, useCopyVersionMutation, useCreateVersionMutation } from "fond/api";
import { useFeatureFlag } from "fond/featureFlags";
import { TextField } from "fond/form/fields";
import { useTask } from "fond/hooks/useTask";
import { AnyObject } from "fond/types";
import { useAppDispatch } from "fond/utils/hooks";
import { generateUnusedName } from "fond/utils/naming";
import { required } from "fond/utils/validation";
import { Message as MessageWidget, Modal } from "fond/widgets";

import CardButton from "../../import/modal/CardButton";
import { setVersion } from "../redux";

import { SetupContainer } from "../../import/modal/ImportModal/ImportModal.styles";

interface IFormData {
  Name: string;
}

interface IProps {
  /**
   * The ID of the project currently being viewed
   */
  projectId: string;
  /**
   * Whether the project being viewed is a planner project
   */
  planner: boolean;
  /**
   * The latest Version.ID for this project
   */
  versionId: string;
  /**
   * Callback function to handle the onClose of the modal
   */
  onClose(): void;
  /**
   * Optional Callback function for when the form is submitted
   */
  onSubmit?(values: IFormData): void;
  /**
   * Optional array of existing version names to check against
   */
  versionNames?: string[];
}

enum Settings {
  BLANK = "BLANK",
  COPY = "COPY",
}

const CreateVersionModal: React.FC<IProps> = ({ projectId, planner, versionId, onClose, onSubmit, versionNames }: IProps) => {
  const dispatch = useAppDispatch();
  const [createVersion, { isLoading: isSaving, isError }] = useCreateVersionMutation();
  const [copyVersion] = useCopyVersionMutation();
  const [copyVersionTaskId, setCopyVersionTaskId] = useState<string | null>(null);
  const {
    task: copyVersionTask,
    isRunning: copyVersionRunning,
    isError: copyVersionError,
    isSuccess: copyVersionSuccess,
  } = useTask({ id: copyVersionTaskId });
  const { value: showCopyCollaborationVersionOption } = useFeatureFlag("copy_collaboration_version");

  const [settings, setSettings] = useState<Settings>(Settings.BLANK);
  let submit: (event?: Partial<Pick<React.SyntheticEvent, "preventDefault" | "stopPropagation">>) => Promise<AnyObject | undefined> | undefined;
  const { enqueueSnackbar } = useSnackbar();

  // Close the modal if copying a version is successful
  useEffect(() => {
    if (!copyVersionTask || copyVersionRunning) {
      return;
    }

    if (copyVersionSuccess) {
      dispatch(
        apiSlice.util.invalidateTags([
          { type: "Versions", id: projectId },
          { type: "Version", id: versionId },
        ])
      );
      onClose();
      enqueueSnackbar("Version successfully created");
    }

    if (copyVersionError) {
      onClose();
      enqueueSnackbar("Failed to create version", { variant: "error" });
    }
  }, [copyVersionTask, copyVersionSuccess, copyVersionRunning, copyVersionError, onClose, enqueueSnackbar, dispatch, projectId, versionId]);

  /**
   * Callback function for the Create button within the modal actions
   */
  const handleOnClick = (event: React.MouseEvent<EventTarget>) => {
    // Submits the React-Final-Form (which handles validation & calling onSubmit is validation passes)
    submit();
  };

  /**
   * On submit function called when the form is submitted and valid
   */
  const handleOnSubmit = async ({ Name }: IFormData) => {
    if (!planner && settings === Settings.COPY) {
      try {
        const task = await copyVersion({
          versionId,
          name: Name,
        }).unwrap();
        setCopyVersionTaskId(task.ID);
      } catch (e) {
        enqueueSnackbar("Failed to create version", { variant: "error" });
      }
    } else {
      createVersion({
        versionId,
        name: Name,
        projectId,
        method: planner && settings === Settings.COPY ? "copy_design" : undefined,
      })
        .unwrap()
        .then(({ ID }) => {
          dispatch(setVersion(ID));
          onClose();
          enqueueSnackbar("Version successfully created");
        })
        .catch(() => {
          enqueueSnackbar("Failed to create version", { variant: "error" });
        });
    }
  };

  if (copyVersionRunning) {
    return (
      <Modal
        open
        header="Copying version"
        content={
          <Box>
            <LinearProgress />
            <Typography sx={{ mt: 1.5, color: "text.secondary" }}>This might take a few minutes.</Typography>
          </Box>
        }
      />
    );
  }

  return (
    <Modal
      open
      header="Create version"
      data-testid="create-version-modal"
      content={
        <Box>
          {isError && (
            <Box mb={2}>
              <MessageWidget type="error">There was an issue creating a version. Please try again.</MessageWidget>
            </Box>
          )}
          <Form<IFormData>
            onSubmit={onSubmit || handleOnSubmit}
            render={({ handleSubmit }) => {
              submit = handleSubmit;
              return (
                <form onSubmit={handleSubmit}>
                  <SetupContainer>
                    <Box width={424}>
                      <TextField
                        inputProps={{ "data-testid": "new-version-name-input" }}
                        autoFocus
                        name="Name"
                        defaultValue={generateUnusedName("Version", versionNames || [])}
                        required
                        label="Name"
                        fullWidth
                        validate={required}
                      />
                    </Box>
                    {(planner || showCopyCollaborationVersionOption) && (
                      <>
                        <CardButton
                          sx={{ width: 424, height: "4em" }}
                          data-testid="new-version-blank"
                          icon={<Computer color="primary" sx={{ margin: "auto" }} />}
                          title="Blank version"
                          subtitle="Start from scratch"
                          selected={settings === Settings.BLANK}
                          onClick={() => {
                            setSettings(Settings.BLANK);
                          }}
                        />
                        <CardButton
                          sx={{ width: 424, height: "4em" }}
                          data-testid="new-version-copy"
                          icon={<CopyAll color="primary" sx={{ margin: "auto" }} />}
                          title="Continue from most recent version"
                          subtitle={planner ? "Copy architecture, input, and design data" : "Copy layers, features, and styles"}
                          selected={settings === Settings.COPY}
                          onClick={() => {
                            setSettings(Settings.COPY);
                          }}
                        />
                      </>
                    )}
                  </SetupContainer>
                </form>
              );
            }}
          />
        </Box>
      }
      actions={
        <>
          <Button data-testid="create-version-cancel-button" color="primary" onClick={onClose} sx={{ marginRight: 1 }}>
            Cancel
          </Button>
          <LoadingButton data-testid="create-version-save-button" color="primary" onClick={handleOnClick} loading={isSaving}>
            Submit
          </LoadingButton>
        </>
      }
    />
  );
};

export default CreateVersionModal;
